import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { onAuthStateChanged, type User } from "firebase/auth";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { auth, db, call } from "./firebase";
import type { Member } from "./domain";

interface Session { user: User | null; member: Member | null; loading: boolean; error: string | null }
const Ctx = createContext<Session>({ user: null, member: null, loading: true, error: null });
export const useSession = () => useContext(Ctx);

const NOT_MEMBER = "Tài khoản này chưa được cấp quyền vào AHG Hub. Vui lòng liên hệ Quản trị (anh Khang) để được thêm vào.";
const LOCKED = "Tài khoản của bạn đã bị khóa. Vui lòng liên hệ Quản trị nếu cần mở lại.";

export function SessionProvider({ children }: { children: ReactNode }) {
  const [s, set] = useState<Session>({ user: null, member: null, loading: true, error: null });
  useEffect(() => {
    let stopMember: (() => void) | undefined;
    const stopAuth = onAuthStateChanged(auth, async (user) => {
      stopMember?.(); stopMember = undefined;
      if (!user?.email) return set({ user, member: null, loading: false, error: null });
      const ref = doc(db, "members", user.email.toLowerCase());
      try {
        // Lần đầu chủ hệ thống đăng nhập: máy chủ tự tạo hồ sơ Quản trị. Người chưa được cấp quyền: máy chủ ghi nhật ký.
        const first = await getDoc(ref).catch(() => null);
        if (!first?.exists() || !first.data().active) await call("whoami")({}).catch(() => undefined);
      } catch { /* bỏ qua */ }
      // Theo dõi hồ sơ trực tiếp: Quản trị đổi vai trò, cơ sở hoặc khóa tài khoản thì có hiệu lực ngay (L5)
      stopMember = onSnapshot(ref, (snap) => {
        if (!snap.exists()) return set({ user, member: null, loading: false, error: NOT_MEMBER });
        const m = snap.data() as Member;
        if (!m.active) return set({ user, member: null, loading: false, error: LOCKED });
        set({ user, member: m, loading: false, error: null });
      }, () => set({ user, member: null, loading: false, error: NOT_MEMBER }));
    });
    return () => { stopAuth(); stopMember?.(); };
  }, []);
  return <Ctx.Provider value={s}>{children}</Ctx.Provider>;
}
